import {
  createContext, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { isLyrraOne } from '@/lib/platform';
import {
  fetchSiteByCustomHost,
  isAppPlatformHostname,
  type CustomDomainSiteInfo,
} from '@/lib/lyrraone-custom-domain';
import { resolveUploadsUrl } from '@/lib/resolve-uploads-url';

interface CustomDomainSiteContextValue {
  loading: boolean;
  isCustomDomain: boolean;
  siteSlug: string | null;
  siteName: string | null;
  creatorId: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  whiteLabel: boolean;
}

const CustomDomainSiteContext = createContext<CustomDomainSiteContextValue>({
  loading: false,
  isCustomDomain: false,
  siteSlug: null,
  siteName: null,
  creatorId: null,
  logoUrl: null,
  faviconUrl: null,
  whiteLabel: false,
});

function applyFavicon(href: string | null) {
  if (typeof document === 'undefined') return;
  const id = 'lo-custom-domain-favicon';
  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (!href) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.id = id;
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
}

export function CustomDomainSiteProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(isLyrraOne());
  const [info, setInfo] = useState<CustomDomainSiteInfo | null>(null);

  useEffect(() => {
    if (!isLyrraOne()) {
      setLoading(false);
      return;
    }
    const host = window.location.hostname;
    if (isAppPlatformHostname(host)) {
      setLoading(false);
      return;
    }
    void fetchSiteByCustomHost(host).then((data) => {
      setInfo(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!info?.faviconUrl && !info?.logoUrl) {
      applyFavicon(null);
      return;
    }
    const raw = info.faviconUrl || info.logoUrl;
    applyFavicon(raw ? resolveUploadsUrl(raw) : null);
    return () => applyFavicon(null);
  }, [info?.faviconUrl, info?.logoUrl]);

  const value = useMemo((): CustomDomainSiteContextValue => ({
    loading,
    isCustomDomain: Boolean(info?.siteSlug),
    siteSlug: info?.siteSlug ?? null,
    siteName: info?.siteName ?? null,
    creatorId: info?.creatorId ?? null,
    logoUrl: info?.logoUrl ? resolveUploadsUrl(info.logoUrl) : null,
    faviconUrl: info?.faviconUrl ? resolveUploadsUrl(info.faviconUrl) : null,
    whiteLabel: info?.whiteLabel === true || Boolean(info?.siteSlug),
  }), [loading, info]);

  return (
    <CustomDomainSiteContext.Provider value={value}>
      {children}
    </CustomDomainSiteContext.Provider>
  );
}

export function useCustomDomainSite() {
  return useContext(CustomDomainSiteContext);
}
